import { createTheme } from '@mui/material/styles';

/**
 * Palette Material You derivata da palette.jpg (AGESCI Zona Galatea).
 * Toni viola monochromatici del manifesto di inaugurazione.
 */
const colors = {
  primary: '#4A2E80',
  onPrimary: '#FFFFFF',
  primaryContainer: '#D8C7F0',
  onPrimaryContainer: '#2A1548',
  secondary: '#7B5BB1',
  onSecondary: '#FFFFFF',
  secondaryContainer: '#E8DCF8',
  onSecondaryContainer: '#2F1A52',
  tertiary: '#5C4B8A',
  onTertiary: '#FFFFFF',
  tertiaryContainer: '#E6DEFF',
  onTertiaryContainer: '#1E1238',
  surface: '#F3EAFD',
  surfaceContainer: '#FFFFFF',
  surfaceContainerLow: '#F8F2FF',
  surfaceContainerHigh: '#EDE0FA',
  onSurface: '#1E1238',
  onSurfaceVariant: '#4F4266',
  outline: '#7B6E94',
  outlineVariant: '#D0C3E3',
  error: '#BA1A1A',
  onError: '#FFFFFF',
  errorContainer: '#FFDAD6',
  onErrorContainer: '#410002',
};

export const theme = createTheme({
  cssVariables: true,
  palette: {
    mode: 'light',
    primary: {
      main: colors.primary,
      contrastText: colors.onPrimary,
      light: colors.secondary,
      dark: '#2A1548',
    },
    secondary: {
      main: colors.secondary,
      contrastText: colors.onSecondary,
      light: colors.primaryContainer,
      dark: colors.tertiary,
    },
    error: {
      main: colors.error,
      contrastText: colors.onError,
    },
    background: {
      default: colors.surface,
      paper: colors.surfaceContainer,
    },
    text: {
      primary: colors.onSurface,
      secondary: colors.onSurfaceVariant,
    },
    divider: colors.outlineVariant,
  },
  shape: {
    borderRadius: 16,
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: { fontWeight: 700, letterSpacing: '-0.02em' },
    h2: { fontWeight: 700, letterSpacing: '-0.01em' },
    h3: { fontWeight: 600 },
    h4: { fontWeight: 600 },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 },
    button: { textTransform: 'none', fontWeight: 600 },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: colors.surface,
          minHeight: '100dvh',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          paddingInline: 20,
          paddingBlock: 10,
          minHeight: 40,
          boxShadow: 'none',
          '&:hover': { boxShadow: 'none' },
        },
        sizeSmall: {
          borderRadius: 16,
          paddingInline: 14,
          paddingBlock: 6,
          minHeight: 36,
        },
        sizeLarge: {
          borderRadius: 24,
          paddingInline: 28,
          paddingBlock: 12,
          minHeight: 48,
        },
        contained: {
          backgroundColor: colors.primary,
          color: colors.onPrimary,
          '&:hover': { backgroundColor: '#3A2268' },
        },
        outlined: {
          borderColor: colors.outlineVariant,
          borderWidth: 1.5,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 28,
          boxShadow: 'none',
          border: `1px solid ${colors.outlineVariant}`,
          backgroundColor: colors.surfaceContainer,
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: 24,
          '&:last-child': { paddingBottom: 24 },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
        rounded: { borderRadius: 28 },
      },
    },
    MuiTextField: {
      defaultProps: { variant: 'outlined' },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 16,
            backgroundColor: colors.surfaceContainerLow,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          backgroundColor: colors.surfaceContainerLow,
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          backgroundColor: colors.surfaceContainerLow,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 12, fontWeight: 600 },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 16 },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: { padding: '16px 24px 24px', gap: 8 },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: colors.primary,
          color: colors.onPrimary,
          boxShadow: 'none',
        },
      },
    },
    MuiFab: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          borderRadius: 20,
          textTransform: 'none',
          fontWeight: 600,
        },
        extended: {
          borderRadius: 20,
          paddingInline: 20,
          height: 56,
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          borderRadius: '12px !important',
          textTransform: 'none',
          fontWeight: 600,
          borderColor: colors.outlineVariant,
          '&.Mui-selected': {
            backgroundColor: colors.primaryContainer,
            color: colors.onPrimaryContainer,
            '&:hover': { backgroundColor: colors.secondaryContainer },
          },
        },
      },
    },
  },
});

export { colors };
